/**
 * Literal multi-keyword matcher built with an Aho-Corasick automaton.
 *
 * It lets the CMM page scan a title once instead of calling includes/replace
 * once for every forbidden word or brand.
 */
export function createKeywordMatcher(keywords, ruleKind = "过滤") {
  if (!Array.isArray(keywords)) {
    throw new TypeError(`${ruleKind}规则列表格式不正确，请刷新后重试。`);
  }

  const nodes = [{ children: new Map(), failure: 0, longestMatchLength: 0 }];
  const uniqueKeywords = new Set();

  for (let index = 0; index < keywords.length; index += 1) {
    const keyword = keywords[index];
    assertUsableKeyword(keyword, ruleKind, index);
    uniqueKeywords.add(keyword);
  }

  uniqueKeywords.forEach(keyword => addKeyword(nodes, keyword));
  buildFailureLinks(nodes);

  return {
    containsAny(text) {
      if (typeof text !== "string" || text.length === 0 || nodes[0].children.size === 0) {
        return false;
      }

      let state = 0;
      // Deliberately scan UTF-16 code units, matching .NET string semantics exactly.
      for (let index = 0; index < text.length; index += 1) {
        state = advance(nodes, state, text[index]);
        if (nodes[state].longestMatchLength > 0) {
          return true;
        }
      }
      return false;
    },

    removeAll(text) {
      if (typeof text !== "string" || text.length === 0 || nodes[0].children.size === 0) {
        return text;
      }

      let removals = null;
      let state = 0;

      for (let index = 0; index < text.length; index += 1) {
        state = advance(nodes, state, text[index]);
        const matchLength = nodes[state].longestMatchLength;
        if (matchLength > 0) {
          // Every other match ending here is a suffix of this longest match, so its
          // range is already covered. This keeps removal strictly linear in title size.
          if (removals === null) {
            removals = new Int32Array(text.length + 1);
          }
          removals[index - matchLength + 1] += 1;
          removals[index + 1] -= 1;
        }
      }

      if (removals === null) {
        return text;
      }

      let activeRemovals = 0;
      const cleaned = [];
      for (let index = 0; index < text.length; index += 1) {
        activeRemovals += removals[index];
        if (activeRemovals === 0) {
          cleaned.push(text[index]);
        }
      }
      return cleaned.join("");
    }
  };
}

function addKeyword(nodes, keyword) {
  let state = 0;

  for (let index = 0; index < keyword.length; index += 1) {
    const character = keyword[index];
    let next = nodes[state].children.get(character);
    if (typeof next === "undefined") {
      next = nodes.length;
      nodes[state].children.set(character, next);
      nodes.push({ children: new Map(), failure: 0, longestMatchLength: 0 });
    }
    state = next;
  }
  nodes[state].longestMatchLength = Math.max(nodes[state].longestMatchLength, keyword.length);
}

function buildFailureLinks(nodes) {
  const queue = [];
  nodes[0].children.forEach(childIndex => {
    nodes[childIndex].failure = 0;
    queue.push(childIndex);
  });

  for (let cursor = 0; cursor < queue.length; cursor += 1) {
    const parentIndex = queue[cursor];
    const parent = nodes[parentIndex];
    parent.children.forEach((childIndex, character) => {
      let failure = parent.failure;
      while (failure !== 0 && !nodes[failure].children.has(character)) {
        failure = nodes[failure].failure;
      }

      const fallback = nodes[failure].children.get(character);
      if (typeof fallback !== "undefined" && fallback !== childIndex) {
        failure = fallback;
      } else {
        failure = 0;
      }

      const child = nodes[childIndex];
      child.failure = failure;
      child.longestMatchLength = Math.max(child.longestMatchLength, nodes[failure].longestMatchLength);
      queue.push(childIndex);
    });
  }
}

function advance(nodes, state, character) {
  while (true) {
    const next = nodes[state].children.get(character);
    if (typeof next !== "undefined") {
      return next;
    }
    if (state === 0) {
      return 0;
    }
    state = nodes[state].failure;
  }
}

function assertUsableKeyword(keyword, ruleKind, index) {
  if (typeof keyword !== "string" || !hasNonWhitespaceCharacter(keyword)) {
    throw new Error(`${ruleKind}规则第 ${index + 1} 条为空或仅包含空白字符，请删除或修正后重试。`);
  }
}

function hasNonWhitespaceCharacter(keyword) {
  for (let index = 0; index < keyword.length; index += 1) {
    if (!isRuleWhitespace(keyword.charCodeAt(index))) {
      return true;
    }
  }
  return false;
}

// Kept in sync with TextKeywordMatcher.cs. Invisible separators cannot be used
// as rules because they would otherwise create dangerous, hard-to-see matches.
function isRuleWhitespace(character) {
  return (character >= 0x0009 && character <= 0x000D)
      || (character >= 0x001C && character <= 0x001F)
      || character === 0x0020
      || character === 0x0085
      || character === 0x00A0
      || character === 0x1680
      || (character >= 0x2000 && character <= 0x200A)
      || character === 0x2028
      || character === 0x2029
      || character === 0x202F
      || character === 0x205F
      || character === 0x3000
      || character === 0xFEFF;
}
